// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import api from '../api/client';
import { setAccessToken } from '../auth/auth-store';
import { useAllClubsMode } from '../hooks/useCombinedData';

type ClubSelection = {
  id: string;
  name: string;
};

type MemberClubEntry = {
  club_id: string;
  club_name: string;
  member_id: string;
  roles: string[];
  joined_at: string;
};

type Props = {
  currentClub: ClubSelection;
  className?: string;
};

export default function ClubSwitcher({ currentClub, className }: Props) {
  const [clubs, setClubs] = useState<MemberClubEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { allClubs, setAllClubs } = useAllClubsMode();

  // Fetch clubs once on mount
  useEffect(() => {
    let cancelled = false;
    api
      .get<MemberClubEntry[]>('/members/me/clubs')
      .then((res) => {
        if (!cancelled) {
          setClubs(res.data);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoaded(true);
          // On error, clubs stays empty — renders as single-club (static text)
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleSwitch = useCallback(
    async (clubId: string) => {
      // Close dropdown first
      setOpen(false);

      // Don't switch to current club
      if (clubId === currentClub.id) return;

      try {
        const res = await api.post<{
          access_token: string;
          expires_in: number;
          member: { id: string; club_id: string };
        }>('/auth/member/switch-club', { club_id: clubId });

        // Store new access token before reload
        setAccessToken(res.data.access_token, res.data.expires_in);

        // Update session in localStorage so SessionContext picks it up after reload
        const sessionRaw = localStorage.getItem('clubview_session');
        if (sessionRaw) {
          try {
            const session = JSON.parse(sessionRaw);
            const targetClub = clubs.find((c) => c.club_id === clubId);
            if (targetClub) {
              session.club = { id: targetClub.club_id, name: targetClub.club_name };
              session.member_id = targetClub.member_id;
              session.roles = targetClub.roles;
              session.role = targetClub.roles[0] || session.role;
              localStorage.setItem('clubview_session', JSON.stringify(session));
              localStorage.setItem('clubview_selected_club', targetClub.club_id);
              localStorage.setItem('clubview_selected_club_name', targetClub.club_name);
              localStorage.setItem('clubview_selected_role', targetClub.roles[0] || '');
            }
          } catch {
            // Ignore parse errors — reload will fix state
          }
        }

        // Full reload for clean state
        window.location.reload();
      } catch {
        // On error, silently fail — user can retry
      }
    },
    [currentClub.id, clubs]
  );

  const isMultiClub = loaded && clubs.length > 1;

  const handleAllClubs = useCallback(() => {
    setOpen(false);
    setAllClubs(true);
    window.location.reload();
  }, [setAllClubs]);

  const handleSwitchClub = useCallback(
    (clubId: string) => {
      setAllClubs(false); // Exit "All Clubs" mode
      handleSwitch(clubId);
    },
    [handleSwitch, setAllClubs]
  );

  const displayName = allClubs ? 'All Clubs' : currentClub.name;

  // While loading or single-club: render static text
  if (!isMultiClub) {
    return (
      <div className={className}>
        <p className="truncate">{currentClub.name}</p>
      </div>
    );
  }

  // Multi-club: render interactive switcher
  return (
    <div className={`relative ${className || ''}`} ref={containerRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1 truncate"
        aria-label={displayName}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate">{displayName}</span>
        <ChevronDown
          className="h-3 w-3 flex-shrink-0"
          aria-hidden="true"
          data-testid="club-switcher-chevron"
        />
      </button>

      {open && (
        <div
          data-testid="club-switcher-dropdown"
          className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          role="listbox"
          aria-label="Select club"
        >
          {/* All Clubs option */}
          <button
            data-testid="club-item-all"
            role="option"
            aria-selected={allClubs}
            onClick={handleAllClubs}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-slate-50 ${
              allClubs ? 'bg-brand-50' : ''
            }`}
          >
            <span className="flex h-5 w-5 items-center justify-center rounded bg-brand-100 text-xs font-bold text-brand-700">
              {clubs.length}
            </span>
            <span className="font-medium text-slate-900">All Clubs</span>
          </button>

          <hr className="my-1 border-slate-100" />

          {clubs.map((club) => {
            const isCurrent = !allClubs && club.club_id === currentClub.id;
            return (
              <button
                key={club.club_id}
                data-testid={`club-item-${club.club_id}`}
                role="option"
                aria-selected={isCurrent}
                onClick={() => handleSwitchClub(club.club_id)}
                className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm transition hover:bg-slate-50 ${
                  isCurrent ? 'bg-brand-50' : ''
                }`}
              >
                <span className="font-medium text-slate-900">{club.club_name}</span>
                <span className="flex gap-1 mt-0.5">
                  {club.roles.map((role) => (
                    <span
                      key={role}
                      className="text-xs rounded bg-slate-100 px-1.5 py-0.5 text-slate-600"
                    >
                      {role}
                    </span>
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
